import { apiUrl, chatBase } from "@/api/client";

import type { WidgetParams } from "./params";

type FormSubmissionBody = {
  session_id: string;
  message_id: string | null;
  form_id: string;
  schema_version: string;
  mode: "inline";
  action: Record<string, unknown>;
  action_token: string | null;
  values: Record<string, unknown>;
  consent_text: string | null;
  idempotency_key: string | null;
};

export function buildFormSubmissionBody(
  sessionId: string,
  messageId: string | null,
  action: Record<string, unknown>,
): FormSubmissionBody {
  const formId =
    typeof action.form_id === "string"
      ? action.form_id
      : typeof action.formId === "string"
        ? action.formId
        : "";
  const values =
    action.values && typeof action.values === "object"
      ? (action.values as Record<string, unknown>)
      : {};
  const declaredAction = (() => {
    if (typeof action.action_json !== "string") return null;
    try {
      const parsed: unknown = JSON.parse(action.action_json);
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  })();
  const controlKeys = new Set([
    "form_id",
    "formId",
    "schema_version",
    "action_json",
    "action_token",
    "actionToken",
    "values",
    "consent_text",
    "idempotency_key",
    "idempotencyKey",
  ]);
  const actionContext = Object.fromEntries(
    Object.entries(action).filter(([key]) => !controlKeys.has(key)),
  );

  return {
    session_id: sessionId,
    message_id: messageId,
    form_id: formId,
    schema_version:
      typeof action.schema_version === "string"
        ? action.schema_version
        : "form.v1",
    mode: "inline",
    action: declaredAction ? { ...actionContext, ...declaredAction } : actionContext,
    action_token:
      typeof action.action_token === "string"
        ? action.action_token
        : typeof action.actionToken === "string"
          ? action.actionToken
          : null,
    values,
    consent_text:
      typeof action.consent_text === "string" ? action.consent_text : null,
    idempotency_key:
      typeof action.idempotency_key === "string"
        ? action.idempotency_key
        : typeof action.idempotencyKey === "string"
          ? action.idempotencyKey
          : null,
  };
}

export async function submitWidgetForm(
  params: Pick<WidgetParams, "tenant" | "chatbot">,
  sessionId: string | null,
  messageId: string | null,
  action: Record<string, unknown>,
): Promise<void> {
  if (!params.tenant || !params.chatbot) return;
  if (!sessionId) return;
  const body = buildFormSubmissionBody(sessionId, messageId, action);
  if (!body.form_id) return;

  const response = await fetch(
    apiUrl(chatBase(params.tenant, params.chatbot) + "/form-submissions"),
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw new Error(`Form submission failed (${response.status})`);
  }
}
